import {
  BaseIcon,
  Button,
  FormWarning,
  PasswordStrength,
} from "@shltelemedicine/shl-ui-lib";
import lockIcon from "@shltelemedicine/shl-ui-lib/dist/assets/lock.svg";
import { useEffect, useRef, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import resetPasswordIcon from "../../../assets/buttons/reset_password.svg";
import passwordUpdatedIcon from "../../../assets/settings/password_updated.svg";
import { Constants, passwordPattern } from "../../../constants";
import useAlert from "../../../hooks/useAlert";
import usersService from "../../../services/users.service";
import LocalStorageUtil from "../../../utils/localStorage";
import PasswordRules from "../../ChangePassword/PasswordRules/PasswordRules";
import AlertModal from "../../Shared/AlertModal/AlertModal";
import ConfirmationModal from "../../Shared/ConfirmationModal/ConfirmationModal";
import TextFormInput from "../../Shared/TextInput/TextFormInput";
import styles from "./SettingsPassword.module.css";

enum UpdatePasswordFormKeys {
  oldPassword = "oldPassword",
  newPassword = "newPassword",
  passwordConfirmation = "passwordConfirmation",
}

type UpdatePasswordFormInputs = {
  [key in UpdatePasswordFormKeys]: string;
};

const SettingsPassword = () => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const {
    handleSubmit,
    control,
    setError,
    formState: {
      errors: formErrors,
      isDirty,
      dirtyFields: { passwordConfirmation: isPasswordConfirmationDirty },
    },
    watch,
    reset,
  } = useForm<UpdatePasswordFormInputs>();

  useEffect(() => {
    reset();
  }, [reset]);

  const {
    showErrorAlert,
    alertProps,
    alertMessage,
    getAlertMessageAndSupportContent,
  } = useAlert();
  const [showPasswordConfirmModal, setShowPasswordConfirmModal] = useState(
    false
  );

  const passwordRef = useRef<string>("");
  passwordRef.current = watch(UpdatePasswordFormKeys.newPassword, "");

  const email = LocalStorageUtil.get(Constants.USERNAME_KEY);

  const resetPasswordHandler = async ({
    oldPassword,
    newPassword,
  }: UpdatePasswordFormInputs) => {
    setLoading(true);
    try {
      const {
        success,
        errorCode,
      } = await usersService.changeCurrentUserPassword({
        oldPassword,
        newPassword,
      });
      if (!success) {
        if (errorCode && Number(errorCode) === 400) {
          setError("passwordConfirmation", {
            type: "pattern",
            message: intl.messages[
              "SETTINGS.VALID_PASSWORD_REQUIRED"
            ] as string,
          });
          return;
        }
        const { message, support } = getAlertMessageAndSupportContent(
          "ERROR.RESET_PASSWORD",
          "An error occurred while trying to update your password.",
          {
            br: () => <br></br>,
          },
          errorCode
        );
        showErrorAlert(message, support);
        return;
      }

      setShowPasswordConfirmModal(true);
      reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <Form onSubmit={handleSubmit(resetPasswordHandler)}>
        <Form.Row className="mb-3">
          <div className="d-flex align-items-center">
            <BaseIcon className="" icon={lockIcon} />
            <span className="mx-1"></span>
            <p className={`${styles.title} mb-0`}>
              <FormattedMessage
                id="SETTINGS.UPDATE_PASSWORD"
                defaultMessage="Update Password"
              />
            </p>
          </div>
        </Form.Row>
        <Form.Row className="mb-2 ">
          <div className={`${styles.label} col`}>
            <div className="w-25">
              <Form.Label className="mb-1 font-md align-items-center">
                <FormattedMessage id="USER.EMAIL" defaultMessage="Email" />
              </Form.Label>
            </div>
          </div>
          <div className="px-2">{email}</div>
        </Form.Row>
        <Form.Row className="mb-2 ">
          <div
            className={`${styles.label} d-lg-flex justify-content-lg-center col align-items-center flex-grow-1`}
          >
            <div className="w-25">
              <Form.Label className="mb-1 font-md align-items-center">
                <FormattedMessage
                  id="CHANGE_PASSWORD.OLD_PASSWORD"
                  defaultMessage="Old Password"
                />
              </Form.Label>
            </div>
            <div className="w-100">
              <TextFormInput
                name={"oldPassword"}
                control={control}
                rules={{
                  required: true,
                  minLength: 8,
                  pattern: passwordPattern,
                }}
                input={{
                  type: "password",
                  isInvalid: !!formErrors?.oldPassword,
                  disabled: loading,
                }}
              />
              {!!formErrors?.oldPassword && (
                <FormWarning
                  className="font-sm flex-grow-1 mt-2"
                  message={
                    <FormattedMessage
                      id={`${
                        formErrors?.oldPassword?.type === "pattern" ||
                        formErrors?.oldPassword?.type === "minLength"
                          ? "SETTINGS.VALID_PASSWORD_REQUIRED"
                          : "SETTINGS.PASSWORD_REQUIRED"
                      }`}
                      defaultMessage={`${
                        formErrors?.oldPassword?.type === "pattern" ||
                        formErrors?.oldPassword?.type === "minLength"
                          ? "Insert Valid Password"
                          : "Insert Password"
                      }`}
                    />
                  }
                />
              )}
            </div>
          </div>
        </Form.Row>
        <Form.Row className="mb-2 ">
          <div
            className={`${styles.label} d-lg-flex justify-content-lg-center col align-items-center flex-grow-1`}
          >
            <div className="w-25">
              <Form.Label className="mb-1 font-md align-items-center">
                <FormattedMessage
                  id="CHANGE_PASSWORD.NEW_PASSWORD"
                  defaultMessage="New Password"
                />
              </Form.Label>
            </div>
            <div className="w-100">
              <TextFormInput
                name={"newPassword"}
                control={control}
                rules={{
                  required: true,
                  minLength: 8,
                  pattern: passwordPattern,
                }}
                input={{
                  type: "password",
                  isInvalid: !!formErrors?.newPassword,
                  disabled: loading,
                }}
              />
              {!!formErrors?.newPassword && (
                <FormWarning
                  className="font-sm flex-grow-1 mt-2"
                  message={
                    <FormattedMessage
                      id={`${
                        formErrors?.newPassword?.type === "pattern" ||
                        formErrors?.newPassword?.type === "minLength"
                          ? "SETTINGS.VALID_PASSWORD_REQUIRED"
                          : "SETTINGS.PASSWORD_REQUIRED"
                      }`}
                      defaultMessage={`${
                        formErrors?.newPassword?.type === "pattern" ||
                        formErrors?.newPassword?.type === "minLength"
                          ? "Insert Valid Password"
                          : "Insert Password"
                      }`}
                    />
                  }
                />
              )}
            </div>
          </div>
        </Form.Row>

        <Form.Row className="mb-2">
          <div
            className={`${styles.label} d-lg-flex justify-content-lg-center col align-items-center flex-grow-1`}
          >
            <div className="w-25">
              <Form.Label className="mb-1 font-md align-items-center ">
                <FormattedMessage
                  id="CHANGE_PASSWORD.RETYPE_PASSWORD"
                  defaultMessage="Retype Password"
                />
              </Form.Label>
            </div>
            <div className="w-100">
              <TextFormInput
                name={"passwordConfirmation"}
                control={control}
                rules={{
                  required: true,
                  validate: {
                    passwordMismatch: (value) => value === passwordRef.current,
                  },
                }}
                input={{
                  type: "password",
                  isInvalid: !!formErrors?.passwordConfirmation,
                  disabled: loading,
                }}
              />
              {!!formErrors?.passwordConfirmation && (
                <FormWarning
                  className="font-sm flex-grow-1 mt-2"
                  message={
                    <FormattedMessage
                      id={"SETTINGS.VALID_PASSWORD_REQUIRED"}
                      defaultMessage={"Insert Valid Password"}
                    />
                  }
                />
              )}
            </div>
          </div>
        </Form.Row>
        <Form.Row className="d-flex justify-content-end px-1">
          <Button
            type="submit"
            icon={{
              src: resetPasswordIcon,
              width: 22,
              height: 22,
            }}
            bgColor="transparent linear-gradient(180deg, #505F95 0%, #2C449A 100%) 0% 0% no-repeat padding-box"
            className="capitalized"
            disabled={!isDirty || !isPasswordConfirmationDirty}
          >
            <FormattedMessage
              id="SECURITY.RESET_PASSWORD"
              defaultMessage="Reset Password"
            />
            {loading && (
              <>
                &nbsp;&nbsp;
                <Spinner as="span" animation="border" size="sm" />
              </>
            )}
          </Button>
        </Form.Row>
        <Form.Row className="my-2">
          <div className="col flex-grow-1">
            <Form.Label className="mb-1 font-md align-items-center text-nowrap">
              <FormattedMessage
                id="PASSWORD_STRENGTH"
                defaultMessage="Password Strength"
              />
            </Form.Label>

            <PasswordStrength password={passwordRef.current} />
          </div>
        </Form.Row>
        <Form.Row className="d-flex align-items-center">
          <div className="col">
            <PasswordRules />
          </div>
        </Form.Row>
      </Form>
      {showPasswordConfirmModal && (
        <ConfirmationModal
          show={showPasswordConfirmModal}
          okBtn={{
            onClick: () => {
              setShowPasswordConfirmModal(false);
            },
          }}
          title={
            <FormattedMessage
              id="SETTINGS.PASSWORD_UPDATED"
              defaultMessage="Password Updated"
            />
          }
          onClose={() => setShowPasswordConfirmModal(false)}
          headerIcon={passwordUpdatedIcon}
        >
          <FormattedMessage
            id="SETTINGS.PASSWORD_UPDATED_SUCCESSFULLY"
            defaultMessage="Password Updated Successfully"
          />
        </ConfirmationModal>
      )}
      <AlertModal {...alertProps}>{alertMessage}</AlertModal>
    </div>
  );
};

export default SettingsPassword;
